import {
	createFund,
	updateFund,
} from '@dealstack/frontend/shared/dataExchange/services/fund.service.ts';
import {
	useTranslationAdminDashboard,
	useTranslationCommonForm,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateFundFormValues, CreateFundShape, CREATE_FUND_FORM } from './types.ts';
import { FundWithDealRes } from '@dealstack/shared/lib/generated';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';

export type FundCreateController = ReturnType<typeof useFundCreateController>;

export const useFundCreateController = ({
	callback,
	fund,
	organizationId,
}: {
	callback?: (fundId?: string) => unknown;
	fund?: FundWithDealRes | null;
	organizationId: string;
}) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const tCommonForm = useTranslationCommonForm();
	const createFundRequest = useMutation({
		mutationFn: (variables: Parameters<typeof createFund>[0]) => createFund(variables),
	});
	const updateFundRequest = useMutation({
		mutationFn: (variables: Parameters<typeof updateFund>[0]) => updateFund(variables),
	});

	const createFundForm = useForm<CreateFundFormValues>({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: zodResolver(CreateFundShape(tCommonForm)),
	});

	const onSubmit = (formData: CreateFundFormValues) => {
		if (fund) {
			updateFundRequest.mutate(
				{
					fundId: fund.id,
					name: formData[CREATE_FUND_FORM.NAME],
					targetName: formData[CREATE_FUND_FORM.TARGET_NAME],
					currency: formData[CREATE_FUND_FORM.CURRENCY],
				},
				{
					onSuccess: () => {
						showToastV2({
							title: tAdminDashboard('update_fund.toast.success.title'),
							message: tAdminDashboard('update_fund.toast.success.message'),
						});
						callback?.();
					},
					onError: (error) => {
						showToastV2({
							state: 'error',
							message:
								getUserFacingErrorMessage(error) ??
								tAdminDashboard('update_fund.toast.error.message'),
						});
					},
				}
			);
		} else {
			createFundRequest.mutate(
				{
					organizationId,
					name: formData[CREATE_FUND_FORM.NAME],
					targetName: formData[CREATE_FUND_FORM.TARGET_NAME],
					currency: formData[CREATE_FUND_FORM.CURRENCY],
				},
				{
					onSuccess: (response) => {
						showToastV2({
							title: tAdminDashboard('create_fund.toast.success.title'),
							message: tAdminDashboard('create_fund.toast.success.message'),
						});
						callback?.(response?.payload?.id);
					},
					onError: (error) => {
						showToastV2({
							state: 'error',
							message:
								getUserFacingErrorMessage(error) ??
								tAdminDashboard('create_fund.toast.error.message'),
						});
					},
				}
			);
		}
	};

	return {
		createFundForm,
		createFundRequest,
		updateFundRequest,
		onSubmit,
	};
};
